import React, { memo, useState } from 'react';
import {
    withErrorProvider,
    withErrorProviderPropTypes,
    withLoaderProvider,
    withLoaderStatePropTypes,
} from '@paysera/react-common';
import compose from 'recompose/compose';
import PropTypes from 'prop-types';
import { Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { SubmissionError } from 'redux-form';

import { RECIPIENTS } from '../config/constants';
import ContactUsForm from './ContactUsForm';
import useSendContactForm from '../hooks/useSendContactForm';
import prepareServerValidationError from '../services/prepareServerValidationError';
import prepareAttachments from '../services/prepareAttachments';
import buildContactSubject from '../services/buildContactSubject';

const ContactUs = ({
    recipient,
    loaderHandler,
    errorHandler,
}) => {
    const { t } = useTranslation('PayseraContactFormBundle');
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const onSuccess = () => setShowSuccessAlert(true);
    const onError = (errors) => {
        throw new SubmissionError(prepareServerValidationError(errors, recipient));
    };

    const buildRequest = ({
        name,
        email,
        product,
        message,
        companyName,
        clientId,
        attachments,
    }) => {
        const request = {
            email,
            subject: buildContactSubject(recipient, { companyName, clientId }),
            message,
            recipient,
            attachments: prepareAttachments(attachments || []),
        };

        if (recipient === RECIPIENTS.support) {
            return { ...request, name };
        }

        return { ...request, company_name: companyName, product };
    };

    const handleSubmit = async (values) => {
        await useSendContactForm(buildRequest(values), loaderHandler, errorHandler, onSuccess, onError);
    };

    const getDescription = () => (recipient === RECIPIENTS.support ? t('contact_form.description.type_support') : '');

    if (showSuccessAlert) {
        return (
            <Alert bsStyle="success">
                {t('contact_form.sent_successfully')}
            </Alert>
        );
    }

    return (
        <>
            <p>{getDescription()}</p>
            <ContactUsForm
                onSubmit={handleSubmit}
                recipient={recipient}
            />
        </>
    );
};

ContactUs.propTypes = {
    recipient: PropTypes.string.isRequired,
    ...withLoaderStatePropTypes,
    ...withErrorProviderPropTypes,
};

export default compose(
    memo,
    withErrorProvider,
    withLoaderProvider,
)(ContactUs);
